// src/app/change-password.jsx
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../apiConfig.js';

const getToken = () => {
  try {
    return localStorage.getItem('userToken');
  } catch {
    return null;
  }
};

export default function ChangePassword() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Add refs for the input elements
  const currentPasswordRef = useRef(null);
  const newPasswordRef = useRef(null);
  const confirmPasswordRef = useRef(null);

  const handleChangePassword = async () => {
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      window.alert('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      window.alert('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      window.alert('New password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      const token = getToken();
      await axios.put(`${API_BASE_URL}/change-password`, {
        currentPassword,
        newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      window.alert('Password changed successfully!');
      navigate('/settings');
    } catch (error) {
      console.error('Error changing password:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to change password. Please check your current password and try again.';
      window.alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e, nextRef) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (nextRef && nextRef.current) {
        nextRef.current.focus();
      } else {
        handleChangePassword();
      }
    }
  };

  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: 10,
    fontFamily: 'system-ui, -apple-system, sans-serif'
  };

  const formBoxStyle = {
    backgroundColor: '#fff',
    padding: 40,
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: 450,
    border: '1px solid #e5e7eb'
  };

  const headerStyle = {
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 30,
    textAlign: 'center',
    color: '#1f2937',
  };

  const fieldContainerStyle = {
    marginBottom: 20,
  };

  const labelStyle = {
    fontWeight: 'bold',
    fontSize: 14,
    color: '#374151',
    marginBottom: 6,
    display: 'block'
  };

  const inputStyle = {
    width: '100%',
    border: '2px solid #e5e7eb',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#fff',
    fontSize: 16,
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  };

  const buttonContainerStyle = {
    marginTop: 30,
  };

  const buttonStyle = {
    padding: 14,
    borderRadius: 8,
    textAlign: 'center',
    marginBottom: 12,
    border: 'none',
    cursor: 'pointer',
    fontSize: 16,
    fontWeight: 'bold',
    width: '100%',
    transition: 'all 0.2s'
  };

  const saveButtonStyle = {
    ...buttonStyle,
    backgroundColor: loading ? '#9ca3af' : '#3b82f6',
    color: '#fff',
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#6b7280',
    color: '#fff',
  };

  return (
    <div style={containerStyle}>
      <div style={formBoxStyle}>
        <h1 style={headerStyle}>Change Password</h1>
        
        <div style={fieldContainerStyle}>
          <label style={labelStyle}>Current Password:</label>
          <input
            ref={currentPasswordRef}
            style={inputStyle}
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Enter current password"
            autoComplete="current-password"
            onKeyPress={(e) => handleKeyPress(e, newPasswordRef)}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <div style={fieldContainerStyle}>
          <label style={labelStyle}>New Password:</label>
          <input
            ref={newPasswordRef}
            style={inputStyle}
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            autoComplete="new-password"
            onKeyPress={(e) => handleKeyPress(e, confirmPasswordRef)}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <div style={fieldContainerStyle}>
          <label style={labelStyle}>Confirm New Password:</label>
          <input
            ref={confirmPasswordRef}
            style={inputStyle}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            autoComplete="new-password"
            onKeyPress={(e) => handleKeyPress(e, null)}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <div style={buttonContainerStyle}>
          <button 
            style={saveButtonStyle}
            onClick={handleChangePassword}
            disabled={loading}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#2563eb';
                e.target.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#3b82f6';
                e.target.style.transform = 'translateY(0)';
              }
            }}
          >
            {loading ? 'Changing...' : 'Change Password'}
          </button>

          <button 
            style={cancelButtonStyle}
            onClick={() => navigate('/settings')}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#4b5563';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#6b7280';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}